import { useCallback, useMemo, useRef } from "react";
import type {
  AgentAction,
  AgentCommandMessage,
  AgentCommandResult,
  IframeToParentMessage,
} from "../../../shared/liveBrowserRuntime/protocol";
import type { LiveFeature } from "./feature";

const READY_TIMEOUT_MS = 10_000;

// Backstop for a command whose port stays attached but never replies; the
// port-cleanup drain already covers reload/navigation/unmount. Generous so a
// long action sequence isn't cut off mid-run.
const SEND_TIMEOUT_MS = 20_000;

export type { AgentAction, AgentCommandResult };

// The tail of `AgentCommandMessage` minus the wire-envelope fields
// (`type` and `id`) that `send` fills in itself. A discriminated union so
// TypeScript keeps each `cmd`'s extra fields tied to its kind.
type AgentCommandPayload =
  | { cmd: "inspect"; selector?: string }
  | { cmd: "console" }
  | { cmd: "screenshot" }
  | { cmd: "interact"; actions: AgentAction[]; selector?: string };

export type AgentControl = {
  feature: LiveFeature;
  // Resolves once the iframe has attached its port and reported ready.
  whenReady: () => Promise<void>;
  inspect: (selector?: string) => Promise<AgentCommandResult>;
  getConsole: () => Promise<AgentCommandResult>;
  interact: (
    actions: AgentAction[],
    selector?: string
  ) => Promise<AgentCommandResult>;
  screenshot: () => Promise<AgentCommandResult>;
};

// The agent's counterpart to useInspectMode: opens a request/response
// channel to the live-browser runtime's agent-control module and exposes
// promise-returning commands over the MessagePort. The runtime bundle is
// injected by the Edge service; this hook just talks to it.
export default function useAgentControl(): AgentControl {
  const portRef = useRef<MessagePort | null>(null);
  const readyRef = useRef(false);
  const readyWaitersRef = useRef<(() => void)[]>([]);
  const pendingRef = useRef(
    new Map<
      string,
      {
        resolve: (result: AgentCommandResult) => void;
        timer: ReturnType<typeof setTimeout>;
      }
    >()
  );
  const idRef = useRef(0);

  const settle = useCallback((id: string, result: AgentCommandResult) => {
    const entry = pendingRef.current.get(id);
    if (!entry) return;
    clearTimeout(entry.timer);
    pendingRef.current.delete(id);
    entry.resolve(result);
  }, []);

  // Rejects rather than hanging if the iframe never reports ready — e.g.
  // the page loads outside preview mode so no runtime is injected. The
  // pre-flight in driveLiveStep should catch that first; this is the
  // backstop.
  const whenReady = useCallback(
    () =>
      new Promise<void>((resolve, reject) => {
        if (readyRef.current) {
          resolve();
          return;
        }
        const timer = setTimeout(() => {
          readyWaitersRef.current = readyWaitersRef.current.filter(
            (w) => w !== waiter
          );
          reject(new Error("The page preview did not finish loading."));
        }, READY_TIMEOUT_MS);
        const waiter = () => {
          clearTimeout(timer);
          resolve();
        };
        readyWaitersRef.current.push(waiter);
      }),
    []
  );

  const send = useCallback(
    (command: AgentCommandPayload) =>
      new Promise<AgentCommandResult>((resolve) => {
        const port = portRef.current;
        if (!port) {
          resolve({ error: "Preview is not ready" });
          return;
        }
        const id = `cmd-${idRef.current++}`;
        const timer = setTimeout(
          () =>
            settle(id, {
              error: "The command timed out without a response from the page.",
            }),
          SEND_TIMEOUT_MS
        );
        pendingRef.current.set(id, { resolve, timer });
        try {
          const message: AgentCommandMessage = {
            type: "3b-agent-cmd",
            id,
            ...command,
          };
          port.postMessage(message);
        } catch {
          settle(id, { error: "Failed to send command" });
        }
      }),
    [settle]
  );

  const feature = useMemo<LiveFeature>(
    () => ({
      attach: (port) => {
        portRef.current = port;
        const onMessage = (event: MessageEvent) => {
          const data = event.data as IframeToParentMessage | undefined;
          if (!data) return;
          if (data.type === "3b-agent-ready") {
            readyRef.current = true;
            for (const w of readyWaitersRef.current) w();
            readyWaitersRef.current = [];
            return;
          }
          if (data.type === "3b-agent-result") {
            const { type: _type, id: _id, ...result } = data;
            settle(data.id, result);
          }
        };
        port.addEventListener("message", onMessage);
        port.start();
        return () => {
          port.removeEventListener("message", onMessage);
          portRef.current = null;
          readyRef.current = false;
          // The document this port belonged to is gone (reload, navigation,
          // or unmount), so any command still awaiting a `3b-agent-result`
          // will never get one. Settle them now instead of hanging forever.
          const pending = pendingRef.current;
          pendingRef.current = new Map();
          for (const { resolve, timer } of pending.values()) {
            clearTimeout(timer);
            resolve({
              error: "The page reloaded before this command finished.",
            });
          }
        };
      },
    }),
    [settle]
  );

  const inspect = useCallback(
    (selector?: string) => send({ cmd: "inspect", selector }),
    [send]
  );

  const getConsole = useCallback(() => send({ cmd: "console" }), [send]);

  const interact = useCallback(
    (actions: AgentAction[], selector?: string) =>
      send({ cmd: "interact", actions, selector }),
    [send]
  );

  const screenshot = useCallback(() => send({ cmd: "screenshot" }), [send]);

  return { feature, whenReady, inspect, getConsole, interact, screenshot };
}
